import { createContext, useContext, useEffect, useRef, ReactNode } from 'react';
import { useWebSocket } from '../hooks/useWebSocket';
import { useUsers } from './UserContext';
import { useEvents } from './EventsContext';
import { useAvailability } from './AvailabilityContext';
import { usePeriods } from './PeriodContext';
import { useHolidays } from './HolidayContext';
import { useAuth } from './AuthContext';
import { invalidateCache } from '../lib/api/httpClient';
import toast from 'react-hot-toast';

interface WebSocketContextType {
  isConnected: () => boolean;
  joinCalendarDate: (date: string) => void;
  leaveCalendarDate: (date: string) => void;
}

const WebSocketContext = createContext<WebSocketContextType>({
  isConnected: () => false,
  joinCalendarDate: () => {},
  leaveCalendarDate: () => {},
});

interface WebSocketProviderProps {
  children: ReactNode;
}

export function WebSocketProvider({ children }: WebSocketProviderProps) {
  const { token } = useAuth();
  const { currentUser, refreshUsers, updateUserSettings } = useUsers();
  const { refreshEvents, updateEvent, addEvent, deleteEvent } = useEvents();
  const { refreshAvailability } = useAvailability();
  const { refreshPeriods } = usePeriods();
  const { clearCache: clearHolidayCache } = useHolidays();
  const { on, off, isConnected, joinCalendarDate, leaveCalendarDate } = useWebSocket();

  // Batching and debouncing state
  const pendingRefreshesRef = useRef<Set<'users' | 'events' | 'availability' | 'periods'>>(new Set());
  const refreshTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const DEBOUNCE_DELAY = 300; // 300ms window

  // Process batched refreshes
  const processBatchedRefreshes = async () => {
    const refreshes = Array.from(pendingRefreshesRef.current);
    pendingRefreshesRef.current.clear();

    // Execute refreshes in parallel
    const promises: Promise<void>[] = [];
    
    if (refreshes.includes('users')) {
      promises.push(refreshUsers());
    }
    if (refreshes.includes('events')) {
      promises.push(refreshEvents());
    }
    if (refreshes.includes('availability')) {
      promises.push(refreshAvailability());
    }
    if (refreshes.includes('periods')) {
      promises.push(refreshPeriods());
    }

    await Promise.all(promises);
  };

  // Queue a refresh with debouncing
  const queueRefresh = (...types: Array<'users' | 'events' | 'availability' | 'periods'>) => {
    // Add types to pending set
    types.forEach(type => pendingRefreshesRef.current.add(type));

    // Clear existing timeout
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
    }

    // Set new timeout
    refreshTimeoutRef.current = setTimeout(() => {
      processBatchedRefreshes();
      refreshTimeoutRef.current = null;
    }, DEBOUNCE_DELAY);
  };

  useEffect(() => {
    if (!token || !currentUser) return;

    // Handle event changes - selective refresh or update
    const handleEventChange = (data: {
      action: 'created' | 'updated' | 'deleted';
      event: any;
      timestamp: string;
    }) => {
      // Invalidate event cache
      invalidateCache(/events/);
      
      // Selective update for single event instead of full refresh
      if (data.action === 'created') {
        addEvent(data.event);
      } else if (data.action === 'updated') {
        updateEvent(data.event);
      } else if (data.action === 'deleted') {
        deleteEvent(data.event.id);
      }
      
      // Don't queue full refresh - selective update is enough
    };

    // Handle availability changes - selective refresh
    const handleAvailabilityChange = (data: {
      userId: string;
      availability: any;
      timestamp: string;
    }) => {
      // Invalidate user and availability caches
      invalidateCache(/users/);
      invalidateCache(/availability/);
      
      // Only show notification if it's not the current user
      if (data.userId !== currentUser.id) {
        toast.success('Colleague updated their availability', {
          duration: 3000,
          icon: '⏰'
        });
      }

      // Queue availability and users refresh
      queueRefresh('availability', 'users');
    };

    // Handle user settings changes - selective update
    const handleUserSettingsChange = (data: {
      userId: string;
      settings: any;
      timestamp: string;
    }) => {
      // Invalidate user cache
      invalidateCache(/users/);
      
      // Selective update for single user settings
      updateUserSettings(data.userId, data.settings);
      
      // Don't queue full refresh if it's just settings - selective update is enough
    };

    // Handle user connection status
    const handleUserConnected = (data: { userId: string; email: string }) => {
      // Could show a subtle notification or update UI to show online status
    };

    const handleUserDisconnected = (data: { userId: string; email: string }) => {
      // Could update UI to show offline status
    };

    // Handle notifications
    const handleNotification = (data: any) => {
      if (data.type === 'info') {
        toast.success(data.message, { duration: 4000 });
      } else if (data.type === 'warning') {
        toast.error(data.message, { duration: 5000 });
      }
    };

    // Handle periods changes - refresh data when admin updates periods and holiday caches
    const handlePeriodsChange = (data: {
      year: number;
      site: string;
      timestamp: string;
    }) => {
      invalidateCache(/periods/);
      invalidateCache(/holidays/);
      
      // Show notification if the site matches current user's site
      if (data.site === currentUser.site) {
        toast.success('Period settings were updated', {
          duration: 3000,
          icon: '📋'
        });

        // Queue periods refresh
        queueRefresh('periods');
      }
    };

    // Handle desiderata changes
    const handleDesiderataChange = (data: {
      userId: string;
      periodId: string;
      timestamp: string;
    }) => {
      // Invalidate desiderata cache
      invalidateCache(/desiderata/);
      
      // Queue events refresh (desiderata affect events)
      queueRefresh('events');
    };

    // Register event listeners
    on('event:changed', handleEventChange);
    on('availability:changed', handleAvailabilityChange);
    on('user:settings:changed', handleUserSettingsChange);
    on('user:connected', handleUserConnected);
    on('user:disconnected', handleUserDisconnected);
    on('notification', handleNotification);
    on('periods:changed', handlePeriodsChange);
    on('desiderata:changed', handleDesiderataChange);

    // Cleanup listeners on unmount
    return () => {
      off('event:changed', handleEventChange);
      off('availability:changed', handleAvailabilityChange);
      off('user:settings:changed', handleUserSettingsChange);
      off('user:connected', handleUserConnected);
      off('user:disconnected', handleUserDisconnected);
      off('notification', handleNotification);
      off('periods:changed', handlePeriodsChange);
      off('desiderata:changed', handleDesiderataChange);

      // Clear pending timeout
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
    };
  }, [token, currentUser, on, off, refreshUsers, refreshEvents, refreshAvailability, refreshPeriods, 
      addEvent, updateEvent, deleteEvent, updateUserSettings, clearHolidayCache]);

  const contextValue = {
    isConnected,
    joinCalendarDate,
    leaveCalendarDate,
  };

  return (
    <WebSocketContext.Provider value={contextValue}>
      {children}
    </WebSocketContext.Provider>
  );
}

export const useWebSocketContext = () => useContext(WebSocketContext);